import { mapSizeToString } from '@containers/adminPanel/utils';
import { getImageAltText } from '@containers/Details/utils';
import {
  translateColorToBulgarian,
  translateTypeToBulgarian
} from '@features/cart/utils';
import { MailGunResponse } from '@models/mailgun';
import { MyPosSuccessData, OrderShippingInfo } from '@models/order';
import { ProductType } from '@models/product';
import { PromoCode } from '@models/promoCode';
import { ShippingData } from '@models/shipping';

import { CartProduct } from '../domain/mappers/cartProductMapper';
import { bgnToEur } from './bgnToEur';

export enum OrderEmailType {
  ADMIN_ORDER = 'ADMIN_ORDER',
  CUSTOMER_ORDER_CREATED = 'CUSTOMER_ORDER_CREATED',
  CUSTOMER_ORDER_SENT = 'CUSTOMER_ORDER_SENT'
}

export interface EmailProps {
  to?: string;
  subject: string;
  text: string;
  html: string;
}
export type EmailOrderSuccessProps = EmailProps &
  MyPosSuccessData & { emailType: OrderEmailType };

export const sendOrderEmailToCustomer = async (
  callback: (props: EmailOrderSuccessProps) => Promise<void>,
  myPosSuccessData: MyPosSuccessData,
  orderNumber: string,
  shippingInfo: OrderShippingInfo,
  items: CartProduct[],
  promoCode: PromoCode | null,
  totalPrice: number,
  shipping: ShippingData
) => {
  const emailProps: EmailOrderSuccessProps = {
    ...myPosSuccessData,
    to: shippingInfo.email,
    subject: 'Поръчка от Teniski Varna',
    text: generateCustomerOrderText(
      OrderEmailType.CUSTOMER_ORDER_CREATED,
      orderNumber,
      shippingInfo,
      items,
      promoCode,
      totalPrice,
      shipping
    ),
    html: generateCustomerOrderHtml(
      OrderEmailType.CUSTOMER_ORDER_CREATED,
      orderNumber,
      shippingInfo,
      items,
      promoCode,
      totalPrice,
      shipping
    ),
    emailType: OrderEmailType.CUSTOMER_ORDER_CREATED
  };

  await callback(emailProps);
};

export const sendOrderEmailToAdmin = async (
  callback: (props: EmailOrderSuccessProps) => Promise<void>,
  myPosSuccessData: MyPosSuccessData,
  orderNumber: string,
  shippingInfo: OrderShippingInfo,
  items: CartProduct[],
  promoCode: PromoCode | null,
  totalPrice: number,
  shipping: ShippingData
) => {
  const emailProps: EmailOrderSuccessProps = {
    ...myPosSuccessData,
    subject: `Нова поръчка номер ${orderNumber} на обща стойност ${totalPrice.toFixed(
      2
    )} лв / €${bgnToEur(totalPrice)}.`,
    text: generateAdminOrderText(
      orderNumber,
      shippingInfo,
      items,
      promoCode,
      totalPrice,
      shipping
    ),
    html: generateAdminOrderEmailHtml(
      orderNumber,
      shippingInfo,
      items,
      promoCode,
      totalPrice,
      shipping
    ),
    emailType: OrderEmailType.ADMIN_ORDER
  };

  await callback(emailProps);
};

export const sendOrderErrorEmailToAdmin = async (
  callback: (props: EmailProps) => Promise<MailGunResponse>,
  shippingInfo: OrderShippingInfo,
  items: CartProduct[],
  promoCode: PromoCode | null,
  totalPrice: number,
  shipping: ShippingData,
  error: boolean
) => {
  const emailProps: EmailProps = {
    subject: 'Неуспешна поръчка от Teniski Varna',
    text: 'Неуспешна поръчка',
    html: generateAdminOrderEmailHtml(
      '',
      shippingInfo,
      items,
      promoCode,
      totalPrice,
      shipping,
      error
    )
  };

  await callback(emailProps);
};

export const sendOrderSentEmailToCustomer = async (
  callback: (props: EmailOrderSuccessProps) => Promise<void>,
  myPosSuccessData: MyPosSuccessData,
  orderNumber: string,
  shippingInfo: OrderShippingInfo,
  items: CartProduct[],
  promoCode: PromoCode | null,
  totalPrice: number,
  shipping: ShippingData
) => {
  const emailProps: EmailOrderSuccessProps = {
    ...myPosSuccessData,
    subject: 'Вашата поръчка от Teniski Varna е изпратена',
    text: generateCustomerOrderText(
      OrderEmailType.CUSTOMER_ORDER_SENT,
      orderNumber,
      shippingInfo,
      items,
      promoCode,
      totalPrice,
      shipping
    ),
    to: shippingInfo.email,
    html: generateCustomerOrderHtml(
      OrderEmailType.CUSTOMER_ORDER_SENT,
      orderNumber,
      shippingInfo,
      items,
      promoCode,
      totalPrice,
      shipping
    ),
    emailType: OrderEmailType.CUSTOMER_ORDER_SENT
  };

  await callback(emailProps);
};

export const sendFeedbackEmailToAdmin = async (
  callback: (props: EmailProps) => Promise<MailGunResponse>,
  feedback: string,
  name: string,
  email: string,
  phone: string
) => {
  const emailProps: EmailProps = {
    subject: 'Обратна връзка от Teniski Varna',
    text: generateFeedbackText(name, email, phone, feedback),
    html: generateFeedbackHtml(name, email, phone, feedback)
  };
  return await callback(emailProps);
};

const generateAdminOrderEmailHtml = (
  orderNumber: string,
  shippingInfo: OrderShippingInfo,
  items: CartProduct[],
  promoCode: PromoCode | null,
  totalPrice: number,
  shipping: ShippingData,
  error?: boolean
) => {
  const emailHtml = `
    <div style="background-color: #f8f9fa; padding: 20px; text-align: center; margin-bottom: 30px;">
      <img 
        src="https://firebasestorage.googleapis.com/v0/b/teniski-varna.appspot.com/o/logo%2Flogo-horizontal.png?alt=media&token=5ffb7c7a-4e5a-4f4f-bd8f-a9b4efd47072" 
        alt="Teniski Varna" 
        height="80"
        style="display: block; margin: 0 auto;" />
    </div>

    <div style="padding: 0 20px;">
      ${
        error
          ? `<div style="background-color: #f8d7da; border: 1px solid #f5c6cb; color: #721c24; padding: 15px; border-radius: 5px; margin-bottom: 20px;">
            <h2 style="margin: 0;">⚠️ Неуспешна поръчка</h2>
            <p style="margin: 5px 0 0 0;">Възникна проблем при плащането и поръчката не беше завършена.</p>
          </div>`
          : ''
      }
      
      <h2 style="color: #333; margin-bottom: 10px;">
        ${
          error
            ? 'Детайли за неуспешната поръчка'
            : `Нова поръчка #${orderNumber}`
        }
      </h2>
      
      <div style="background-color: #d4edda; border: 1px solid #c3e6cb; color: #155724; padding: 15px; border-radius: 5px; margin-bottom: 20px;">
        <p style="margin: 0; font-size: 16px;">
          <strong>Обща стойност: ${totalPrice.toFixed(2)}лв / €${bgnToEur(
    totalPrice
  )}</strong>
        </p>
      </div>

      <h3 style="color: #333; margin-top: 30px; margin-bottom: 20px;">Поръчани продукти:</h3>
      ${generateOrderSummayHtml(items)}

      <div style="background-color: #f8f9fa; padding: 20px; margin-top: 30px; border-radius: 5px;">
        <h3 style="color: #333; margin-top: 0;">Обобщение на поръчката</h3>
        <table style="width: 100%; font-size: 16px;">
          <tr>
            <td style="padding: 8px 0; color: #666;">Продукти:</td>
            <td style="padding: 8px 0; text-align: right; color: #333;">${items
              .reduce((sum, item) => sum + item.price, 0)
              .toFixed(2)}лв</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #666;">Доставка:</td>
            <td style="padding: 8px 0; text-align: right; color: #333;">${
              totalPrice >= shipping.minimumAmount
                ? `Безплатно (поръчка над ${shipping.minimumAmount}лв)`
                : `${shipping.shippingCost}лв / €${bgnToEur(
                    shipping.shippingCost
                  )}`
            }</td>
          </tr>
          ${
            promoCode
              ? `
          <tr>
            <td style="padding: 8px 0; color: #666;">Промо код (${promoCode.name}):</td>
            <td style="padding: 8px 0; text-align: right; color: #333;">-${promoCode.percentage}%</td>
          </tr>
          `
              : ''
          }
          <tr style="border-top: 2px solid #dee2e6;">
            <td style="padding: 12px 0; font-size: 18px; color: #333;"><strong>Общо:</strong></td>
            <td style="padding: 12px 0; text-align: right; font-size: 18px; color: #333;"><strong>${totalPrice.toFixed(
              2
            )}лв / €${bgnToEur(totalPrice)}</strong></td>
          </tr>
        </table>
      </div>

      <div style="margin-top: 30px; padding: 20px; background-color: #e8f4f8; border-radius: 5px;">
        <h3 style="color: #333; margin-top: 0;">Информация за доставка</h3>
        <p style="margin: 5px 0; color: #666;"><strong>Клиент:</strong> ${
          shippingInfo.firstName
        } ${shippingInfo.lastName}</p>
        <p style="margin: 5px 0; color: #666;"><strong>Телефон:</strong> ${
          shippingInfo.phone
        }</p>
        <p style="margin: 5px 0; color: #666;"><strong>Имейл:</strong> ${
          shippingInfo.email
        }</p>
        ${
          shippingInfo.speedyOffice
            ? `<p style="margin: 5px 0; color: #666;"><strong>Офис на Speedy:</strong> ${shippingInfo.speedyOffice}</p>`
            : `<p style="margin: 5px 0; color: #666;"><strong>Адрес за доставка:</strong> ${shippingInfo.personalAddress}</p>`
        }
      </div>

      <div style="margin-top: 30px; padding: 20px; background-color: #fff3cd; border: 1px solid #ffeaa7; border-radius: 5px;">
        <h3 style="color: #856404; margin-top: 0;">Следващи стъпки</h3>
        <p style="margin: 5px 0; color: #856404;">1. Свържете се с клиента за потвърждение</p>
        <p style="margin: 5px 0; color: #856404;">2. Подгответе поръчката за изпращане</p>
        <p style="margin: 5px 0; color: #856404;">3. Изпратете имейл за потвърждение на изпращането</p>
      </div>
      
      <div style="margin-top: 30px; padding: 20px; text-align: center; color: #999; font-size: 14px;">
        <p>Този имейл е генериран автоматично от системата на Teniski Varna.</p>
        <p>© ${new Date().getFullYear()} Teniski Varna. Всички права запазени.</p>
      </div>
    </div>
  `;

  return emailHtml;
};

const generateOrderSummayHtml = (items: CartProduct[]) => {
  let itemsHtml = '';

  items.forEach((item) => {
    const isTShirt = item.productType === ProductType.TSHIRT;

    itemsHtml += `
      <div style="display: flex; margin-bottom: 20px; border-bottom: 1px solid #e0e0e0; padding-bottom: 20px;" href="https://teniskivarna.com/products/${
        item.id
      }">
        <img 
          src="${item.image}" 
          alt="${getImageAltText(item.title)}" 
          width="${isTShirt ? 150 : 120}" 
          height="${isTShirt ? 150 : 100}"
          style="${
            !isTShirt ? 'padding: 20px 15px' : ''
          } margin-right: 20px;" />
        <div style="flex: 1;">
          <h3 style="margin: 0 0 10px 0; font-size: 18px; color: #333;">${
            item.title
          }</h3>
          ${
            isTShirt
              ? `<p style="margin: 5px 0; color: #666;">Цвят: ${translateColorToBulgarian(
                  item.color
                )}</p>`
              : ''
          }
          ${
            isTShirt
              ? `<p style="margin: 5px 0; color: #666;">Модел: ${translateTypeToBulgarian(
                  item.type
                )}</p>`
              : ''
          }
          ${
            isTShirt
              ? `<p style="margin: 5px 0; color: #666;">Размер: ${mapSizeToString(
                  item.size
                )}</p>`
              : ''
          }
          <p style="margin: 5px 0; font-weight: bold; color: #333;">Цена: ${item.price.toFixed(
            2
          )}лв / €${bgnToEur(item.price)}</p>
          <p style="margin: 5px 0; color: #666;">Персонализация: ${
            item.customizationNotes ? item.customizationNotes : 'няма'
          }</p>
        </div>
     </div>
    `;
  });

  return itemsHtml;
};

const generateCustomerOrderHtml = (
  type: OrderEmailType,
  orderNumber: string,
  shippingInfo: OrderShippingInfo,
  items: CartProduct[],
  promoCode: PromoCode | null,
  totalPrice: number,
  shipping: ShippingData
) => `
    <div style="background-color: #f8f9fa; padding: 20px; text-align: center; margin-bottom: 30px;">
      <img 
        src="https://firebasestorage.googleapis.com/v0/b/teniski-varna.appspot.com/o/logo%2Flogo-horizontal.png?alt=media&token=5ffb7c7a-4e5a-4f4f-bd8f-a9b4efd47072" 
        alt="Teniski Varna" 
        height="80"
        style="display: block; margin: 0 auto;" />
    </div>

    <div style="padding: 0 20px;">
      ${
        type === OrderEmailType.CUSTOMER_ORDER_CREATED
          ? `
          <h2 style="color: #333; margin-bottom: 10px;">Благодарим ви за поръчката!</h2>
          <p style="color: #666; font-size: 16px; line-height: 1.6;">Уважаеми клиенти,</p>
          <p style="color: #666; font-size: 16px; line-height: 1.6;">Вашата поръчка с номер <strong style="color: #333;">${orderNumber}</strong> беше успешно приета и ще бъде обработена в най-кратък срок. Ще получите имейл с потвърждение, когато поръчката бъде изпратена.</p>
        `
          : `
          <h2 style="color: #333; margin-bottom: 10px;">Вашата поръчка е на път!</h2>
          <p style="color: #666; font-size: 16px; line-height: 1.6;">Уважаеми клиенти,</p>
          <p style="color: #666; font-size: 16px; line-height: 1.6;">Радваме се да ви информираме, че вашата поръчка с номер <strong style="color: #333;">${orderNumber}</strong> беше изпратена и скоро ще бъде доставена на посочения от вас адрес.</p>
        `
      }
      
      <div style="background-color: #fff3cd; border: 1px solid #ffeaa7; border-radius: 5px; padding: 15px; margin: 20px 0;">
        <p style="margin: 0; color: #856404; font-size: 14px;">
          <strong>Важно:</strong> Моля, запазете този имейл за вашите записи. Номерът на поръчката ще ви бъде необходим при евентуални запитвания.
        </p>
      </div>

      <h3 style="color: #333; margin-top: 30px; margin-bottom: 20px;">Детайли на поръчката:</h3>
      ${generateOrderSummayHtml(items)}

      <div style="background-color: #f8f9fa; padding: 20px; margin-top: 30px; border-radius: 5px;">
        <h3 style="color: #333; margin-top: 0;">Обобщение на поръчката</h3>
        <table style="width: 100%; font-size: 16px;">
          <tr>
            <td style="padding: 8px 0; color: #666;">Доставка:</td>
            <td style="padding: 8px 0; text-align: right; font-weight: bold; color: #333;">${
              totalPrice >= shipping.minimumAmount
                ? `Безплатно`
                : `${shipping.shippingCost}лв / €${bgnToEur(
                    shipping.shippingCost
                  )}`
            }</td>
          </tr>
          ${
            promoCode
              ? `
          <tr>
            <td style="padding: 8px 0; color: #666;">Промо код (${promoCode.name}):</td>
            <td style="padding: 8px 0; text-align: right; font-weight: bold; color: #333;">-${promoCode.percentage}%</td>
          </tr>
          `
              : ''
          }
          <tr style="border-top: 2px solid #dee2e6;">
            <td style="padding: 12px 0; font-size: 18px; color: #333;"><strong>Общо:</strong></td>
            <td style="padding: 12px 0; text-align: right; font-size: 18px; color: #333;"><strong>${totalPrice.toFixed(
              2
            )}лв / €${bgnToEur(totalPrice)}</strong></td>
          </tr>
        </table>
      </div>

      <div style="margin-top: 30px; padding: 20px; background-color: #e8f4f8; border-radius: 5px;">
        <h3 style="color: #333; margin-top: 0;">Информация за доставка</h3>
        <p style="margin: 5px 0; color: #666;"><strong>Получател:</strong> ${
          shippingInfo.firstName
        } ${shippingInfo.lastName}</p>
        ${
          shippingInfo.speedyOffice
            ? `<p style="margin: 5px 0; color: #666;"><strong>Офис на Speedy:</strong> ${shippingInfo.speedyOffice}</p>`
            : ''
        }
        ${
          shippingInfo.personalAddress
            ? `<p style="margin: 5px 0; color: #666;"><strong>Адрес за доставка:</strong> ${shippingInfo.personalAddress}</p>`
            : ''
        }
        <p style="margin: 5px 0; color: #666;"><strong>Телефон за връзка:</strong> ${
          shippingInfo.phone
        }</p>
        <p style="margin: 5px 0; color: #666;"><strong>Имейл:</strong> ${
          shippingInfo.email
        }</p>
      </div>

      <div style="margin-top: 40px; padding: 30px 20px; background-color: #333; color: #fff; text-align: center;">
        <h3 style="margin-top: 0; color: #fff;">Нуждаете се от помощ?</h3>
        <p style="color: #ccc; margin: 10px 0;">Нашият екип е на разположение да отговори на всички ваши въпроси.</p>
        <div style="margin-top: 20px;">
          <p style="margin: 5px 0; color: #fff;">📞 Телефон: <strong>088 822 8975</strong></p>
          <p style="margin: 5px 0; color: #fff;">✉️ Имейл: <strong>teniski.varna2022@gmail.com</strong></p>
          <p style="margin: 5px 0; color: #fff;">📍 Адрес: <strong>гр. Варна, бул. Сливница 165, ет. 0</strong></p>
        </div>
      </div>
      
      <div style="margin-top: 30px; padding: 20px; text-align: center; color: #999; font-size: 14px;">
        <p>Този имейл е генериран автоматично. Моля, не отговаряйте на него.</p>
        <p>© ${new Date().getFullYear()} Teniski Varna. Всички права запазени.</p>
      </div>
    </div>
  `;

const generateCustomerOrderText = (
  type: OrderEmailType,
  orderNumber: string,
  shippingInfo: OrderShippingInfo,
  items: CartProduct[],
  promoCode: PromoCode | null,
  totalPrice: number,
  shipping: ShippingData
): string => {
  let text = '';

  if (type === OrderEmailType.CUSTOMER_ORDER_CREATED) {
    text += 'Благодарим ви, че избрахте нашите продукти!\n\n';
    text += `Успешно направихте поръчка с номер: ${orderNumber}\n\n`;
  } else {
    text += `Вашата поръчка с номер ${orderNumber} е изпратена и пътува към вас!\n\n`;
  }

  text += 'ПОРЪЧАНИ ПРОДУКТИ:\n';
  text += '===================\n\n';

  items.forEach((item, index) => {
    const isTShirt = item.productType === ProductType.TSHIRT;
    text += `${index + 1}. ${item.title}\n`;
    if (isTShirt) {
      text += `   Цвят: ${translateColorToBulgarian(item.color)}\n`;
      text += `   Модел: ${translateTypeToBulgarian(item.type)}\n`;
      text += `   Размер: ${mapSizeToString(item.size)}\n`;
    }
    text += `   Цена: ${item.price.toFixed(2)}лв / €${bgnToEur(item.price)}\n`;
    text += `   Персонализация: ${item.customizationNotes || 'няма'}\n\n`;
  });

  text += '\nОБЩА ИНФОРМАЦИЯ:\n';
  text += '================\n';
  text += `Доставка: ${
    totalPrice >= shipping.minimumAmount
      ? 'Безплатно'
      : `${shipping.shippingCost}лв / €${bgnToEur(shipping.shippingCost)}`
  }\n`;
  if (promoCode) {
    text += `Промо код: ${promoCode.name} (${promoCode.percentage}%)\n`;
  }
  text += `Общо: ${totalPrice.toFixed(2)}лв / €${bgnToEur(totalPrice)}\n\n`;

  text += 'ИНФОРМАЦИЯ ЗА ДОСТАВКА:\n';
  text += '======================\n';
  text += `Име: ${shippingInfo.firstName} ${shippingInfo.lastName}\n`;
  if (shippingInfo.speedyOffice) {
    text += `Офис на Speedy: ${shippingInfo.speedyOffice}\n`;
  }
  if (shippingInfo.personalAddress) {
    text += `Личен адрес: ${shippingInfo.personalAddress}\n`;
  }
  text += `Телефон: ${shippingInfo.phone}\n`;
  text += `Имейл: ${shippingInfo.email}\n\n`;

  text += 'АКО ИМАТЕ ВЪПРОСИ:\n';
  text += '==================\n';
  text += 'Телефон: 088 822 8975\n';
  text += 'Имейл: teniski.varna2022@gmail.com\n';
  text += 'Адрес: гр. Варна, бул. Сливница 165, ет. 0, срещу Costa кафе\n\n';

  text += 'Благодарим ви за поръчката!\n';
  text += 'Екипът на Teniski Varna\n';

  return text;
};

const generateAdminOrderText = (
  orderNumber: string,
  shippingInfo: OrderShippingInfo,
  items: CartProduct[],
  promoCode: PromoCode | null,
  totalPrice: number,
  shipping: ShippingData
): string => {
  let text = `НОВА ПОРЪЧКА #${orderNumber}\n`;
  text += '='.repeat(50) + '\n\n';
  text += `Обща стойност: ${totalPrice.toFixed(2)}лв / €${bgnToEur(
    totalPrice
  )}\n\n`;

  text += 'ПОРЪЧАНИ ПРОДУКТИ:\n';
  text += '-----------------\n';

  items.forEach((item, index) => {
    const isTShirt = item.productType === ProductType.TSHIRT;
    text += `\n${index + 1}. ${item.title}\n`;
    if (isTShirt) {
      text += `   Модел: ${translateTypeToBulgarian(item.type)}\n`;
      text += `   Цвят: ${translateColorToBulgarian(item.color)}\n`;
      text += `   Размер: ${mapSizeToString(item.size)}\n`;
    }
    text += `   Цена: ${item.price.toFixed(2)}лв / €${bgnToEur(item.price)}\n`;
    if (item.customizationNotes) {
      text += `   Персонализация: ${item.customizationNotes}\n`;
    }
  });

  text += '\n\nОБОБЩЕНИЕ:\n';
  text += '-----------\n';
  text += `Продукти: ${items
    .reduce((sum, item) => sum + item.price, 0)
    .toFixed(2)}лв\n`;
  text += `Доставка: ${
    totalPrice >= shipping.minimumAmount
      ? `Безплатно (над ${shipping.minimumAmount}лв)`
      : `${shipping.shippingCost}лв`
  }\n`;
  if (promoCode) {
    text += `Промо код (${promoCode.name}): -${promoCode.percentage}%\n`;
  }
  text += `ОБЩО: ${totalPrice.toFixed(2)}лв / €${bgnToEur(totalPrice)}\n`;

  text += '\n\nИНФОРМАЦИЯ ЗА ДОСТАВКА:\n';
  text += '----------------------\n';
  text += `Клиент: ${shippingInfo.firstName} ${shippingInfo.lastName}\n`;
  text += `Телефон: ${shippingInfo.phone}\n`;
  text += `Имейл: ${shippingInfo.email}\n`;
  if (shippingInfo.speedyOffice) {
    text += `Офис на Speedy: ${shippingInfo.speedyOffice}\n`;
  } else {
    text += `Адрес: ${shippingInfo.personalAddress}\n`;
  }

  text += '\n\nСЛЕДВАЩИ СТЪПКИ:\n';
  text += '---------------\n';
  text += '1. Свържете се с клиента за потвърждение\n';
  text += '2. Подгответе поръчката за изпращане\n';
  text += '3. Изпратете имейл за потвърждение на изпращането\n';

  return text;
};

const generateFeedbackText = (
  name: string,
  email: string,
  phone: string,
  feedback: string
): string => {
  let text = 'НОВА ОБРАТНА ВРЪЗКА\n';
  text += '='.repeat(50) + '\n\n';

  text += 'ИНФОРМАЦИЯ ЗА КЛИЕНТА:\n';
  text += '---------------------\n';
  text += `Име: ${name || 'Не е посочено'}\n`;
  text += `Имейл: ${email || 'Не е посочен'}\n`;
  text += `Телефон: ${phone || 'Не е посочен'}\n`;

  text += '\n\nСЪОБЩЕНИЕ:\n';
  text += '-----------\n';
  text += feedback + '\n';

  text += '\n\nПРЕПОРЪЧИТЕЛНИ ДЕЙСТВИЯ:\n';
  text += '------------------------\n';
  text += '• Отговорете на клиента в рамките на 24 часа\n';
  text +=
    '• Ако е необходимо, се свържете по телефон за по-бърза комуникация\n';
  text += '• Запазете това съобщение за вашите записи\n';

  return text;
};

const generateFeedbackHtml = (
  name: string,
  email: string,
  phone: string,
  feedback: string
) => `
  <div style="background-color: #f8f9fa; padding: 20px; text-align: center; margin-bottom: 30px;">
    <img 
      src="https://firebasestorage.googleapis.com/v0/b/teniski-varna.appspot.com/o/logo%2Flogo-horizontal.png?alt=media&token=5ffb7c7a-4e5a-4f4f-bd8f-a9b4efd47072" 
      alt="Teniski Varna" 
      height="80"
      style="display: block; margin: 0 auto;" />
  </div>

  <div style="padding: 0 20px; font-family: Arial, sans-serif;">
    <div style="background-color: #d1ecf1; border: 1px solid #bee5eb; color: #0c5460; padding: 15px; border-radius: 5px; margin-bottom: 20px;">
      <h2 style="margin: 0;">📧 Нова обратна връзка</h2>
      <p style="margin: 5px 0 0 0;">Получихте ново съобщение от клиент чрез формата за контакт.</p>
    </div>

    <div style="background-color: #fff; border: 1px solid #dee2e6; border-radius: 5px; padding: 20px; margin-bottom: 20px;">
      <h3 style="color: #333; margin-top: 0;">Информация за клиента</h3>
      <table style="width: 100%; font-size: 16px;">
        <tr>
          <td style="padding: 8px 0; color: #666; width: 120px;"><strong>Име:</strong></td>
          <td style="padding: 8px 0; color: #333;">${
            name || 'Не е посочено'
          }</td>
        </tr>
        <tr>
          <td style="padding: 8px 0; color: #666;"><strong>Имейл:</strong></td>
          <td style="padding: 8px 0; color: #333;">${
            email || 'Не е посочен'
          }</td>
        </tr>
        <tr>
          <td style="padding: 8px 0; color: #666;"><strong>Телефон:</strong></td>
          <td style="padding: 8px 0; color: #333;">${
            phone || 'Не е посочен'
          }</td>
        </tr>
      </table>
    </div>

    <div style="background-color: #f8f9fa; border-left: 4px solid #007bff; padding: 20px; margin-bottom: 20px;">
      <h3 style="color: #333; margin-top: 0;">Съобщение</h3>
      <p style="color: #333; font-size: 16px; line-height: 1.6; white-space: pre-wrap;">${feedback}</p>
    </div>

    <div style="background-color: #fff3cd; border: 1px solid #ffeaa7; border-radius: 5px; padding: 15px;">
      <h3 style="color: #856404; margin-top: 0;">Препоръчителни действия</h3>
      <p style="margin: 5px 0; color: #856404;">• Отговорете на клиента в рамките на 24 часа</p>
      <p style="margin: 5px 0; color: #856404;">• Ако е необходимо, се свържете по телефон за по-бърза комуникация</p>
      <p style="margin: 5px 0; color: #856404;">• Запазете това съобщение за вашите записи</p>
    </div>
    
    <div style="margin-top: 30px; padding: 20px; text-align: center; color: #999; font-size: 14px;">
      <p>Този имейл е генериран автоматично от системата на Teniski Varna.</p>
      <p>© ${new Date().getFullYear()} Teniski Varna. Всички права запазени.</p>
    </div>
  </div>
`;
